/**
 * IMPORTS
 */

import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as XLSX from "xlsx-js-style";

import { countValidImageFilesFromZip } from "@pages/client/details-model/view-model/helpers/contar-arquivos-validos-zip";
import { handleIsCellEmpty } from "@pages/client/details-model/view-model/helpers/is-cell-empty";
import {
  handleNormalizarLinhaPlanilhaImportada,
  handleObterValorCelulaPorNomeColuna,
  IMetadadoCampoVariavel,
} from "@pages/client/details-model/view-model/helpers/normalizar-linhas-planilhas";
// helpers
import { handleNormalizeImportedCellValue } from "@pages/client/details-model/view-model/helpers/normalize-spreadsheet-cell-value";
import {
  normalizarChaveParaComparacao,
  sanitizarChaveMatricula,
} from "@pages/client/details-model/view-model/helpers/sanitizar-chave-matricula";
import { validarAbasPlanilha } from "@pages/client/details-model/view-model/helpers/validar-abas-planilha";
import { validarConsistenciaArquivosRemessa } from "@pages/client/details-model/view-model/helpers/validar-consistencia-arquivos-remessa";
import {
  getZipEntryFileName,
  isValidImageExtension,
  shouldIgnoreZipEntry,
  VALID_IMAGE_EXTENSIONS,
} from "@pages/client/details-model/view-model/helpers/zip-entry-filter";
// typings
import { IImagem, ITableData } from "@pages/client/details-model/view-model/interface";

// hooks / solicitação de remessa
import { useSolicitarRemessa } from "@/hooks/remessas/solicitar-remessa";
import { ISolicitacaoRemessaPayload } from "@/hooks/remessas/solicitar-remessa/index.d";

import { useStoreZustandModelosCliente } from "@/store-zustand/cliente/details-modelo";
// store-zustand
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

// utils
import { limparDocumento } from "@/utils/format-document";
import { notifyAlert, notifyError, notifySuccess } from "@/utils/notify-messages";
import { handleSentryLogSolicitacaoRemessa } from "@/utils/sentry/handle-sentry-log-solicitacao-remessa";

// quantidade de linhas de dados da planilha modelo já formatadas como texto
const LINHAS_PRE_FORMATADAS_TEXTO = 5000;

const useDetailsModel = () => {
  const navigate = useNavigate();

  const { user } = useStoreZustandUserAuth();

  const { solicitarRemessaAsync, isLoadingSolicitarRemessa } = useSolicitarRemessa();

  const {
    // dados do modelo do cliente
    modeloUnico,

    // modal de solicitação de remessa
    setisModalSolicitacaoRemessa,
    isModalSolicitacaoRemessa,

    // dados da tabela para solicitar remessa
    setDadosRemessa,
    dadosRemessa,

    // dados de responsabilidade
    dadosResponsabilidade,
    setDadosResponsabilidade,

    // file planilha
    setSelectedFile,
    selectedFile,

    // zipFile
    setSelectedZipFile,
    selectedZipFile,

    // responsavel por fazer a limpaeza do estado persistente
    clearPersistedData,
  } = useStoreZustandModelosCliente();

  const obligatory = modeloUnico?.modeloCompleto?.campo_chave ?? "matricula";
  const campos_variaveis_raw = modeloUnico?.modeloCompleto?.campos_variavei_planilhas;
  const campos_variaveis_modelo = modeloUnico?.modeloCompleto?.campos_variaveis;

  const metadadosCamposVariaveis: IMetadadoCampoVariavel[] = (() => {
    if (Array.isArray(campos_variaveis_modelo) && campos_variaveis_modelo.length > 0) {
      const camposOrdenados = [...campos_variaveis_modelo].sort(
        (campoAnterior: { ordem?: number }, campoSeguinte: { ordem?: number }) =>
          (campoAnterior?.ordem ?? 0) - (campoSeguinte?.ordem ?? 0)
      );
      return camposOrdenados
        .map((campo: { nome?: string; obrigatorio?: number | boolean }) => ({
          nome: typeof campo?.nome === "string" ? campo.nome.trim() : "",
          obrigatorio: Number(campo?.obrigatorio) === 1,
        }))
        .filter((campo) => campo.nome.length > 0);
    }
    if (Array.isArray(campos_variaveis_raw) && campos_variaveis_raw.length > 0) {
      return campos_variaveis_raw
        .filter((nome): nome is string => typeof nome === "string" && nome.trim().length > 0)
        .map((nome) => ({ nome: nome.trim(), obrigatorio: true }));
    }
    return [];
  })();

  const campos_variaveis = metadadosCamposVariaveis.map((campo) => campo?.nome);

  // estado para armazenar o arquivo selecionado
  const [isOpenModal, setIsOpenModal] = useState(false);

  // estados para processamento de imagens
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [processingLog, setProcessingLog] = useState<string[]>([]);

  const [qtdModelosProvisirio, setQtdModelosProvisirio] = useState(0);

  // estado para armazenar o termo de filtro (campo matrícula)
  const [filtroMatricula, setFiltroMatricula] = useState("");
  const [dadosOriginais, setDadosOriginais] = useState<ITableData[] | null>(null);

  // Função que atualiza o estado de remessa
  const updateRemessa = useCallback(
    (data: ITableData[], keepOriginal = false) => {
      setDadosRemessa(data);
      if (!keepOriginal) {
        setDadosOriginais(data);
      }
    },
    [setDadosRemessa]
  );

  // Função para aplicar o filtro
  const handleFiltrarMatricula = useCallback(() => {
    // ee o campo estiver vazio, restaurar o conjunto original
    if (filtroMatricula.length === 0) {
      if (dadosOriginais) {
        setDadosRemessa([...dadosOriginais]); // garante nova referência
        notifySuccess("Filtro limpo — exibindo todos os registros novamente!");
      }
      return;
    }

    // aplica o filtro sobre o conjunto original (não o filtrado)
    const base = dadosOriginais ?? dadosRemessa;
    const resultados = base?.filter((row) => {
      const valor = String(row[obligatory] || "").toLowerCase();
      return valor.includes(filtroMatricula.toLowerCase());
    });

    if (!resultados || resultados.length === 0) {
      notifyAlert(`Nenhum resultado encontrado para matrícula ${filtroMatricula}`);
      return;
    }

    updateRemessa(resultados, true);
    notifySuccess(`${resultados.length} registro(s) encontrado(s)!`);
  }, [filtroMatricula, dadosRemessa, dadosOriginais, obligatory, setDadosRemessa, updateRemessa]);

  // Função para baixar a planilha modelo
  const handleDownloadTemplate = () => {
    const templateObj: Record<string, string> = {};

    // campo obrigatório
    templateObj[obligatory] = "";

    // campos variáveis
    campos_variaveis.forEach((campo) => {
      templateObj[campo] = "";
    });

    const headers = Object.keys(templateObj);

    // cria planilha somente com o header (as linhas de dados são pré-formatadas abaixo)
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);

    const headerStyle = {
      font: {
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: {
        fgColor: { rgb: "1F4FD8" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      },
    };

    const range = XLSX.utils.decode_range(worksheet["!ref"]!);

    // estiliza header
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c: C });

      if (!worksheet[addr]) continue;

      worksheet[addr].s = headerStyle;
      worksheet[addr].z = "@";
    }

    // força todas as células das linhas de dados como texto
    // (a lib não suporta formato por coluna nem grava células vazias sem valor,
    // então cada célula é criada com string vazia e formato "@")
    for (let R = 1; R <= LINHAS_PRE_FORMATADAS_TEXTO; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });

        worksheet[addr] = {
          t: "s",
          v: "",
          z: "@", // formato texto no Excel
        };
      }
    }

    worksheet["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: range.s.c },
      e: { r: LINHAS_PRE_FORMATADAS_TEXTO, c: range.e.c },
    });

    // largura das colunas
    worksheet["!cols"] = headers.map(() => ({
      wch: 20,
    }));

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Dados");

    XLSX.writeFile(
      workbook,
      `remessa-${
        modeloUnico?.nome_modelo?.toLocaleLowerCase()?.replace(/\s+/g, "-") ?? "remessa_pedido"
      }.xlsx`
    );
  };

  // Função para processar o arquivo Excel enviado
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }

    setIsOpenModal(true);

    //  limpa o input para permitir selecionar o mesmo arquivo novamente
    event.target.value = "";

    return;
  };

  // Função para enviar o arquivo Excel
  const handleSubmitFile = () => {
    if (dadosRemessa?.length) return;

    updateRemessa([]); // limpar dados antes de importar

    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array", cellDates: true });

      const validacaoAbas = validarAbasPlanilha(workbook.SheetNames);
      if (!validacaoAbas.valido) {
        notifyAlert(validacaoAbas.mensagem);
        setSelectedFile(null);
        return;
      }

      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        raw: true, // mantém texto como texto
        defval: "", // evita undefined
      });

      // normaliza as células e guarda o número real da linha no Excel
      const linhasPlanilha = (jsonData as Array<any>).map((row, index) => {
        const newRow: any = {};

        Object.keys(row).forEach((key) => {
          const valorCelula = row[key];
          const ehColunaChave = key.toLowerCase() === obligatory.toLowerCase();

          // na chave (matrícula/CPF) os espaços Unicode são removidos antes da normalização geral,
          // que os converteria em espaço comum e impediria casar com o nome do arquivo no ZIP
          newRow[key] = handleNormalizeImportedCellValue(
            ehColunaChave && typeof valorCelula === "string"
              ? sanitizarChaveMatricula(valorCelula)
              : valorCelula
          );
        });

        // __rowNum__ (índice 0-based da linha na aba) é preenchido pelo sheet_to_json
        return { row: newRow, linhaExcel: (row.__rowNum__ ?? index + 1) + 1 };
      });

      // ignora linhas totalmente vazias (a planilha modelo vem com linhas pré-formatadas como texto)
      const linhasPreenchidas = linhasPlanilha.filter(({ row }) =>
        Object.values(row).some((valor) => !handleIsCellEmpty(valor))
      );

      // validar os dados (verificar se tem os campos necessários)
      if (linhasPreenchidas.length > 0) {
        // atualizar os dados da tabela
        const formattedData = linhasPreenchidas.map(({ row }) => row);

        const chavesObrigatorias = [
          obligatory,
          ...metadadosCamposVariaveis
            .filter((campo) => campo.obrigatorio)
            .map((campo) => campo.nome),
        ];

        for (let i = 0; i < formattedData.length; i++) {
          const linha = formattedData[i] as Record<string, unknown>;
          for (const nomeColunaObrigatoria of chavesObrigatorias) {
            const valor = handleObterValorCelulaPorNomeColuna(linha, nomeColunaObrigatoria);
            if (handleIsCellEmpty(valor)) {
              notifyAlert(
                `Linha ${linhasPreenchidas[i].linhaExcel} da planilha: preencha todos os campos obrigatórios (campo vazio: "${nomeColunaObrigatoria}").`
              );
              setSelectedFile(null);
              return;
            }
          }
        }

        const normalizedData = formattedData.map((row) =>
          handleNormalizarLinhaPlanilhaImportada(
            row as Record<string, unknown>,
            obligatory,
            metadadosCamposVariaveis
          )
        );

        updateRemessa(normalizedData as ITableData[]);

        notifySuccess("Dados importados com sucesso!");
      } else {
        notifyAlert("O arquivo não contém dados válidos!");
        setSelectedFile(null);
      }
    };

    if (selectedFile) {
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const MAX_ZIP_SIZE_MB = 15;
  const MAX_ZIP_SIZE_BYTES = MAX_ZIP_SIZE_MB * 1024 * 1024;
  const MAX_IMAGE_SIZE_MB = 5;
  const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

  const isValidZipSize = (zipFile: Blob) => zipFile.size <= MAX_ZIP_SIZE_BYTES;

  const notifyZipTooLarge = (zipFileName: string) => {
    notifyAlert(`Arquivo ZIP ${zipFileName} muito grande (máx ${MAX_ZIP_SIZE_MB}MB)!`);
  };

  const notifyUnsupportedFileType = (fileName: string) => {
    notifyAlert(`Arquivo inválido: ${fileName}. Envie apenas arquivos ZIP.`);
  };

  // Função para extrair arquivos ZIP
  async function extractZipFile(
    zipFile: File | Blob
  ): Promise<Array<{ file: File; path: string; name: string; source: string }>> {
    const zipFileName = zipFile instanceof File ? zipFile.name : "selecionado";

    if (!isValidZipSize(zipFile)) {
      notifyZipTooLarge(zipFileName);
      setIsProcessing(false);
      setSelectedZipFile(null);
      handleCleanState();
      return [];
    }

    setSelectedZipFile(zipFile as File);
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
      script.onload = async () => {
        try {
          const JSZip = (window as any).JSZip;
          const zip = new JSZip();
          const zipData = await zip.loadAsync(zipFile);
          const imageFiles = [];

          for (const [relativePath, file] of Object.entries(zipData.files)) {
            const isDirectory = (file as { dir: boolean }).dir;
            if (shouldIgnoreZipEntry(relativePath, isDirectory)) continue;

            const fileName = getZipEntryFileName(relativePath);

            if (!isValidImageExtension(fileName)) {
              notifyAlert(
                `Arquivo inválido no ZIP: ${fileName}. Use apenas ${VALID_IMAGE_EXTENSIONS.map((ext) => ext.toUpperCase()).join(", ")}.`
              );
              setIsProcessing(false);

              setTimeout(() => {
                handleCleanState();
              }, 3000);
              return [];
            }

            const blob = await (file as any).async("blob");

            if (blob.size > MAX_IMAGE_SIZE_BYTES) {
              notifyAlert(
                `A imagem "${fileName}" excede o limite permitido. Cada imagem deve ter no máximo ${MAX_IMAGE_SIZE_MB} MB.`
              );
              setIsProcessing(false);
              setSelectedZipFile(null);
              handleCleanState();
              return [];
            }

            const imageFile = new File([blob], fileName, {
              type: blob.type || "image/jpeg",
            });

            imageFiles.push({
              file: imageFile,
              path: relativePath,
              name: fileName,
              source: "zip",
            });
          }

          resolve(imageFiles as Array<IImagem>);
        } catch (error) {
          reject(error);
        }
      };
      script.onerror = () => reject(new Error("Falha ao carregar JSZip"));
      document.head.appendChild(script);
    });
  }

  // Função que processa o zip de imagens linha a linha
  const handeProcessFileList = async (inputFiles: FileList) => {
    const processedImages: IImagem[] = [];

    // PECORRENDO CADA ARQUIVO DA LISTA DE ARQUIVOS
    for (const currentFile of Array.from(inputFiles)) {
      // VERIFICANDO SE UM ZIP
      if (currentFile.name.toLowerCase().endsWith(".zip")) {
        if (!isValidZipSize(currentFile)) {
          notifyZipTooLarge(currentFile.name);
          setIsProcessing(false);
          setSelectedZipFile(null);
          return [];
        }

        try {
          const extractedZipImages = await extractZipFile(currentFile);

          console.log(
            `📦 Extraídas ${extractedZipImages.length} imagens do ZIP: ${currentFile.name}`
          );

          if (!extractedZipImages.length) {
            if (!isValidZipSize(currentFile)) {
              return [];
            }

            notifyAlert(
              `Arquivo inválido: ${currentFile.name}.
            Use apenas imagens nos formatos: ${VALID_IMAGE_EXTENSIONS.map((ext) =>
              ext.toUpperCase()
            ).join(", ")}`
            );

            setTimeout(() => {
              handleCleanState();
            }, 3000);

            return [];
          }

          processedImages.push(...extractedZipImages);
        } catch (_error) {
          notifyError(`Erro ao processar ZIP ${currentFile.name}!`);
        }
      } else {
        notifyUnsupportedFileType(currentFile.name);
        setIsProcessing(false);
        setSelectedZipFile(null);
        return [];
      }
    }

    return processedImages;
  };

  // Função para converter arquivo em Base64
  const handleToBase64 = (file: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  // Função para processar imagens e associá-las à tabela
  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const nonZipFile = Array.from(files).find((file) => !file.name.toLowerCase().endsWith(".zip"));

    if (nonZipFile) {
      notifyUnsupportedFileType(nonZipFile.name);
      setIsProcessing(false);
      setSelectedZipFile(null);
      setProcessedCount(0);
      setTotalFiles(0);
      setProcessingLog([]);
      return;
    }

    const zipTooLarge = Array.from(files).find(
      (file) => file.name.toLowerCase().endsWith(".zip") && !isValidZipSize(file)
    );

    if (zipTooLarge) {
      notifyZipTooLarge(zipTooLarge.name);
      setSelectedZipFile(null);
      return;
    }

    const temCargaFoto = Number(modeloUnico?.modeloCompleto?.tem_carga_foto) === 1;

    setIsProcessing(true);
    setProcessedCount(0);
    setTotalFiles(0);
    setProcessingLog([]);

    try {
      if (temCargaFoto && (!dadosRemessa || dadosRemessa.length === 0)) {
        notifyError("Importe a planilha antes de enviar as fotos.");
        return;
      }

      let imageFiles = [] as any;

      // Processar FileList do input
      imageFiles = await handeProcessFileList(files);

      if (temCargaFoto && dadosRemessa && imageFiles.length !== dadosRemessa.length) {
        notifyError(
          `A planilha tem ${dadosRemessa.length} registro(s), mas foram enviadas ${imageFiles.length} imagem(ns). A quantidade deve ser igual. Se o ZIP foi criado no macOS, compacte novamente sem metadados ocultos.`
        );
        setSelectedZipFile(null);
        return;
      }

      setTotalFiles(imageFiles.length);
      console.log(`Encontradas ${imageFiles.length} imagens`);

      // LOGAR AS IMAGENS ENCONTRADAS
      const foundImages = imageFiles.map((image: any) => {
        const icon = image.source === "zip" ? "📦" : "📁";
        return `${icon} ${image.path}`;
      });

      setProcessingLog(foundImages);

      const updatedTableData = [...dadosRemessa!];
      let processedImages = 0;

      for (const imageFile of imageFiles) {
        try {
          // Extrair apenas o nome do arquivo sem extensão e sem caminho
          const fileName = imageFile.name;
          const imageName = fileName.replace(/\.[^/.]+$/, "");

          console.log(`Processando: ${imageFile.path} -> ${imageName}`);

          // Procurar correspondência na tabela pelo campo matricula
          const matchingRowIndex = updatedTableData.findIndex((row) => {
            const matricula = normalizarChaveParaComparacao(String(row[obligatory]));
            const imgName = normalizarChaveParaComparacao(imageName);

            return matricula === imgName;
          });

          if (matchingRowIndex !== -1) {
            // Criar URL da imagem
            const imageUrl = (await handleToBase64(imageFile.file)) as string;

            updatedTableData[matchingRowIndex] = {
              ...updatedTableData[matchingRowIndex],
              foto: imageName,
              imageUrl: imageUrl,
              imagePath: imageFile.path,
              imageSource: imageFile.source,
              imageComplete: imageFile,
            };

            processedImages++;
            console.log(
              `✅ ${imageName} → ${updatedTableData[matchingRowIndex][obligatory]} (${imageFile.source})`
            );
          } else {
            console.log(`❌ Sem correspondência: ${imageName}`);

            notifyAlert(`Matrícula '${imageName}' não encontrada na planilha.`);

            setTimeout(() => {
              handleCleanState();
            }, 3000);

            return [];
          }
        } catch (error) {
          console.error(`Erro ao processar ${imageFile.path}:`, error);
        }
      }

      setDadosRemessa(updatedTableData);
      setDadosOriginais(updatedTableData);
      setProcessedCount(processedImages);

      console.log(
        `Processamento concluído: ${processedImages} de ${imageFiles.length} imagens associadas!`
      );
    } catch (_error) {
      notifyError("Erro ao processar pasta/arquivo. Tente novamente!");
      return;
    } finally {
      setIsProcessing(false);
    }
  };

  // Função para deletar uma linha específica da tabela
  const handleDeleteRow = (index: number) => {
    const updatedTableData = [...dadosRemessa!];
    const rowToDelete = updatedTableData[index];

    // Liberar URL do objeto se existir
    if (rowToDelete.imageUrl) {
      URL.revokeObjectURL(rowToDelete.imageUrl);
    }

    // Remover a linha da tabela
    updatedTableData.splice(index, 1);
    updateRemessa(updatedTableData);
  };

  // Função para verificar a orientação do arquivo vertical ou horizontal
  const getOrientationAbbreviation = useCallback((orientation: string): "v" | "h" | null => {
    if (typeof orientation !== "string") return null;

    const value = orientation.toLowerCase();

    if (value === "vertical") return "v";
    if (value === "horizontal") return "h";

    notifyAlert("Orientação inválida!");
    return null;
  }, []);

  // Função para solicitar a remessa
  const handleConfirmarSolicitacao = () => {
    setisModalSolicitacaoRemessa(!isModalSolicitacaoRemessa);
  };

  // Função para limpar o estado
  const handleCleanState = useCallback(() => {
    setDadosRemessa([]);
    setDadosOriginais(null);

    setSelectedFile(null);
    setSelectedZipFile(null);

    setFiltroMatricula("");

    setDadosResponsabilidade(null);

    setIsOpenModal(false);

    setProcessingLog([]);
    setProcessedCount(0);
    setTotalFiles(0);

    setIsProcessing(false);

    updateRemessa([]);
  }, [
    setDadosRemessa,
    setSelectedFile,
    setSelectedZipFile,
    setDadosResponsabilidade,
    updateRemessa,
  ]);

  /**
   * Função para solicitar a remessa
   */
  const handleSolicitarRemessa = useCallback(async () => {
    try {
      if (!dadosResponsabilidade) {
        setIsOpenModal(true);

        return setisModalSolicitacaoRemessa(false);
      }

      const clientePortal =
        user?.tipo_login === "subordinado" ? user?.cliente_principal?.id : user?.id;

      const idSolicitanteSubordinado = user?.tipo_login === "subordinado" ? user?.id : null;
      const idSolicitanteCliente = user?.tipo_login === "user" ? user?.id : null;

      const totalSolicitacao = dadosRemessa?.length;

      const dataSolicitarRemessa = {
        cliente_id: clientePortal,
        user_id_solicitante_remessa: idSolicitanteCliente,
        users_solicitante_subordinado_id: idSolicitanteSubordinado,
        consultor_id: user?.consultor?.id ?? null,
        modelo_tecnico_id: modeloUnico?.modeloCompleto?.id,
        tecnologia_id: modeloUnico?.modeloCompleto?.tecnologia?.id,
        produto_id: modeloUnico?.modeloCompleto?.produto?.id,
        total_solicitacoes: totalSolicitacao! > 0 ? totalSolicitacao : qtdModelosProvisirio,
        situacao: "solicitado",
        status: "envio_de_dados",
        campo_chave: obligatory ? obligatory : null,
        posicao: getOrientationAbbreviation(modeloUnico?.modeloCompleto?.posicionamento as string),

        // dados para responsabilidade
        nome: dadosResponsabilidade?.nome,
        documento: limparDocumento(dadosResponsabilidade?.documento),
      } as unknown as ISolicitacaoRemessaPayload;

      if (selectedZipFile && !isValidZipSize(selectedZipFile)) {
        notifyZipTooLarge(selectedZipFile.name);
        setSelectedZipFile(null);
        setisModalSolicitacaoRemessa(false);
        return;
      }

      /**
       * VALIDAÇÃO PARA SABER SE TEM ZIP FILE
       */

      if (!selectedZipFile && modeloUnico?.modeloCompleto?.tem_carga_foto) {
        notifyError("Por favor, selecione o arquivo ZIP para continuar!");
        setisModalSolicitacaoRemessa(false);
        return;
      }

      // cria o formData para multipart
      const formData = new FormData();

      // adiciona os campos da remessa
      Object.entries(dataSolicitarRemessa).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value as any);
        }
      });

      if (selectedZipFile) {
        formData.append("zip_file", selectedZipFile);
      }

      if (selectedFile) {
        formData.append("csv_file", selectedFile);
      }

      if (
        Number(modeloUnico?.modeloCompleto?.tem_carga_foto) === 1 &&
        (dadosRemessa?.length ?? 0) > 0
      ) {
        const semFoto = dadosRemessa!.some((row) => {
          const complete = (row as Record<string, unknown>).imageComplete;
          return !row.imageUrl && !complete;
        });
        if (semFoto) {
          notifyError("Associe uma foto a cada registro da planilha antes de solicitar a remessa.");
          setisModalSolicitacaoRemessa(false);
          return;
        }
      }

      /**
       * Integridade: total_solicitacoes === planilha === ZIP (apenas quando há upload real)
       */
      const possuiUploadParaValidar = !!selectedFile || !!selectedZipFile;

      if (possuiUploadParaValidar) {
        const totalSolicitacoesEnviado = Number(dataSolicitarRemessa?.total_solicitacoes);
        const qtdCsv = dadosRemessa?.length ?? 0;
        const qtdZip = selectedZipFile ? await countValidImageFilesFromZip(selectedZipFile) : null;

        const resultado = validarConsistenciaArquivosRemessa({
          totalSolicitacoes: totalSolicitacoesEnviado,
          qtdCsv,
          qtdZip,
        });

        if (!resultado.valido) {
          console.warn("[handleSolicitarRemessa] divergência de integridade:", {
            totalSolicitacoesEnviado,
            qtdCsv,
            qtdZip,
            divergencias: resultado.divergencias,
          });
          notifyError(resultado.mensagem);
          setisModalSolicitacaoRemessa(false);
          return;
        }
      }

      /**
       * SOLICITAÇÃO PARA REMESSAS COM ZIP E CSV
       */
      await solicitarRemessaAsync(formData as any, {
        onSuccess: async (response) => {
          const { data } = response;
          setisModalSolicitacaoRemessa(false);

          if (!data?.id) return;

          // LOGAR SOLICITAÇÃO DE REMESSA NO SENTRY
          await handleSentryLogSolicitacaoRemessa({
            user: user,
            payload: dataSolicitarRemessa,
            response: data,
          });

          // LIMPAR ZUZTAND
          clearPersistedData();

          updateRemessa([]);

          navigate("/shipments-in-progress");
          return;
        },
      });

      handleCleanState();
    } catch (_error) {
      setisModalSolicitacaoRemessa(false);
      setDadosResponsabilidade(null);
      handleCleanState();
      return;
    }
  }, [
    clearPersistedData,
    dadosRemessa,
    modeloUnico,
    navigate,
    selectedFile,
    selectedZipFile,
    setisModalSolicitacaoRemessa,
    solicitarRemessaAsync,
    dadosResponsabilidade,
    user,
    setDadosResponsabilidade,
    obligatory,
    qtdModelosProvisirio,
    handleCleanState,
    updateRemessa,
    getOrientationAbbreviation,
  ]);

  return {
    isProcessing,
    totalFiles,
    processingLog,
    obligatory,
    dadosRemessa,
    setIsOpenModal,
    campos_variaveis,
    handleDownloadTemplate,
    handleFileUpload,
    handleImageUpload,
    processedCount,
    handleDeleteRow,
    handleSolicitarRemessa,
    handleSubmitFile,
    isOpenModal,
    modeloUnico,
    handleConfirmarSolicitacao,
    isLoadingSolicitarRemessa,
    setDadosRemessa,
    setFiltroMatricula,
    filtroMatricula,
    handleFiltrarMatricula,
    setQtdModelosProvisirio,
    handleCleanState,
  };
};

/**
 * EXPORTS
 */
export { useDetailsModel };
